import { useState, useRef, useEffect } from 'react';
import { resolveImageUrl } from '@/utils/imageUrl';

const PRESETS = {
  thumb: { w: 150, h: 150 },
  card: { w: 400, h: 300 },
  hero: { w: 1200, h: 675 },
};

/**
 * Build an optimized URL for an uploaded image.
 * External URLs (unsplash, pexels, etc.) are returned as-is.
 */
function buildOptimizedUrl(src, { preset, w, h, q, format } = {}) {
  if (!src) return '';
  const resolved = resolveImageUrl(src);
  if (!resolved) return '';

  // Only optimize our own uploads (contain /api/uploads/)
  if (!resolved.includes('/api/uploads/')) return resolved;

  const url = new URL(resolved, window.location.origin);
  if (preset && PRESETS[preset]) {
    url.searchParams.set('preset', preset);
  } else {
    if (w) url.searchParams.set('w', w);
    if (h) url.searchParams.set('h', h);
  }
  if (q) url.searchParams.set('q', q);
  if (format) url.searchParams.set('format', format);
  return url.toString();
}

/**
 * <OptimizedImage /> — drop-in <img> replacement with:
 *  - Automatic WebP conversion for uploaded images
 *  - Preset sizes (thumb, card, hero)
 *  - Lazy loading with fade-in
 *  - Blur placeholder while loading
 */
export function OptimizedImage({
  src,
  alt = '',
  preset,
  width,
  height,
  quality,
  fallback = '',
  className = '',
  onError,
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  const optimizedSrc = buildOptimizedUrl(src, {
    preset,
    w: width,
    h: height,
    q: quality,
    format: 'webp',
  });

  const finalSrc = error
    ? (fallback ? resolveImageUrl(fallback) || fallback : '')
    : (optimizedSrc || resolveImageUrl(fallback) || fallback);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  // If ref img is already loaded (cached), mark as loaded
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [finalSrc]);

  if (!finalSrc) return null;

  return (
    <img
      ref={imgRef}
      src={finalSrc}
      alt={alt}
      loading="lazy"
      onLoad={() => setLoaded(true)}
      onError={(e) => {
        if (!error) {
          setError(true);
          onError?.(e);
        }
      }}
      className={`${className} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      {...props}
    />
  );
}

export { buildOptimizedUrl, PRESETS };
